import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { AuthService, ConfirmEmailCommand } from '@anihis/core/data-access';

const CLIENT_APP_LOGIN_URL = 'com.anihis.app://login';
const COUNTDOWN_SECONDS = 5;

type ConfirmStatus = 'loading' | 'success' | 'error';

@Component({
  selector: 'anihis-confirm-email',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, TranslocoModule],
  templateUrl: './confirm-email.component.html',
  styleUrl: './confirm-email.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmEmailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);

  status = signal<ConfirmStatus>('loading');
  countdown = signal(COUNTDOWN_SECONDS);
  errorMessage = signal('');

  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const userId = params['userId'] ?? '';
      const token = params['token'] ?? '';
      this.confirmEmail(userId, token);
    });
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  openApp(): void {
    window.location.href = CLIENT_APP_LOGIN_URL;
  }

  private confirmEmail(userId: string, token: string): void {
    if (!userId || !token) {
      this.errorMessage.set(
        'Invalid confirmation link. Please request a new one.',
      );
      this.status.set('error');
      return;
    }

    const command: ConfirmEmailCommand = { userId, token };

    this.authService.authConfirmEmailPost(command).subscribe({
      next: () => {
        this.status.set('success');
        this.startCountdown();
      },
      error: (err: {
        error?: {
          errors?: Record<string, string[]>;
          detail?: string;
          title?: string;
        };
      }) => {
        const errors = err?.error?.errors;
        const msg = errors
          ? Object.values(errors).flat().join(' ')
          : err?.error?.detail ||
            err?.error?.title ||
            'Email confirmation failed. The link may have expired.';
        this.errorMessage.set(msg);
        this.status.set('error');
      },
    });
  }

  private startCountdown(): void {
    this.countdownInterval = setInterval(() => {
      const next = this.countdown() - 1;
      this.countdown.set(next);
      if (next <= 0) {
        clearInterval(this.countdownInterval!);
        this.countdownInterval = null;
        window.location.href = CLIENT_APP_LOGIN_URL;
      }
    }, 1000);
  }
}
